import { Menu } from 'lucide-preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { GlobalFilter } from './filters/GlobalFilter';
import { HeaderActions } from './HeaderActions';
import type { UserInfo } from '../functions/auth/authService';
import type { Client } from '../types/api';
import { getRouteMeta, type AppRoute } from '../routing';

interface HeaderProps {
  user?: UserInfo;
  userName?: string;
  selectedClient?: Client;
  currentPage?: AppRoute;
  onToggleMobileNav?: () => void;
}

function getUserInitials(displayName: string): string {
  const normalizedName = displayName.includes('@') ? displayName.split('@')[0] : displayName;
  const nameParts = normalizedName.trim().split(/[\s._-]+/).filter(Boolean);

  if (nameParts.length === 0) return 'U';
  if (nameParts.length === 1) return nameParts[0].slice(0, 2).toUpperCase();

  return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
}

export const Header = ({
  user,
  userName,
  selectedClient,
  currentPage = 'overview',
  onToggleMobileNav,
}: HeaderProps) => {
  const headerRef = useRef<HTMLElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const routeMeta = getRouteMeta(currentPage);
  const displayName = user?.name || selectedClient?.name || userName || 'User';
  const userInitials = getUserInitials(displayName);
  const roleLabel = user?.is_admin
    ? 'Administrator'
    : user?.is_client_view
      ? 'Client view'
      : 'Investor';

  useEffect(() => {
    const scrollContainer = headerRef.current?.parentElement;
    if (!scrollContainer) return;

    const updateScrolledState = () => {
      setIsScrolled(scrollContainer.scrollTop > 8);
    };

    updateScrolledState();
    scrollContainer.addEventListener('scroll', updateScrolledState, { passive: true });

    return () => scrollContainer.removeEventListener('scroll', updateScrolledState);
  }, []);

  return (
    <header ref={headerRef} className={`header${isScrolled ? ' header--scrolled' : ''}`}>
      <div className="header-left">
        {onToggleMobileNav && (
          <button
            type="button"
            className="mobile-menu-button"
            aria-label="Open navigation menu"
            onClick={onToggleMobileNav}
          >
            <Menu size={20} />
          </button>
        )}

        <div className="header-title-block">
          <p className="header-breadcrumb">
            <span className="header-breadcrumb__root">Pages</span>
            <span className="header-breadcrumb__separator" aria-hidden="true">
              /
            </span>
            {routeMeta.section && (
              <>
                <span className="header-breadcrumb__section">{routeMeta.section}</span>
                <span className="header-breadcrumb__separator" aria-hidden="true">
                  /
                </span>
              </>
            )}
            <span className="header-breadcrumb__current">{routeMeta.breadcrumb}</span>
          </p>
          <h1 className="header-page-title text-page-title">{routeMeta.title}</h1>
        </div>
      </div>

      <div className="header-right">
        <div className="header-controls-capsule">
          {user && <GlobalFilter user={user} currentPage={currentPage} />}
          <HeaderActions currentPage={currentPage} />
          <div className="user-profile">
            <div className="user-avatar" aria-hidden="true">
              <span>{userInitials}</span>
            </div>
            <div className="user-info">
              <span className="user-name">{displayName}</span>
              <span className="user-role">{roleLabel}</span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
